import { useEffect, useState } from "react";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { QrCode, Download, Copy, Printer, UserCheck } from "lucide-react";
import { toast } from "sonner";

/**
 * Office panel on the Job page — QR sign-in poster + live "on site now"
 * register + today's log + CSV export for audits.
 */
export function JobSigninPanel({ job }) {
  const [url, setUrl] = useState("");
  const [qrSrc, setQrSrc] = useState("");
  const [log, setLog] = useState(null);

  const loadLog = () => {
    api.get(`/jobs/${job.id}/signins`).then((r) => setLog(r.data)).catch(() => {});
  };

  useEffect(() => {
    api.post(`/jobs/${job.id}/signin-token`).then((r) => setUrl(r.data.url)).catch(() => {});
    api.get(`/jobs/${job.id}/signin-qr.png`, { responseType: "blob" })
      .then((r) => setQrSrc(URL.createObjectURL(r.data)))
      .catch(() => {});
    loadLog();
    const t = setInterval(loadLog, 60000);
    return () => clearInterval(t);
  }, [job.id]); // eslint-disable-line react-hooks/exhaustive-deps

  const copyUrl = async () => {
    try { await navigator.clipboard.writeText(url); toast.success("Sign-in link copied"); }
    catch { toast.error("Couldn't copy"); }
  };

  const printQr = () => {
    const w = window.open("", "_blank");
    if (!w) return;
    w.document.write(`
      <html><head><title>Site sign-in — ${job.title}</title></head>
      <body style="font-family:Arial,sans-serif;text-align:center;padding:40px">
        <div style="display:inline-block;border:6px solid #09090B;padding:32px 48px">
          <div style="background:#FF5F15;color:#fff;font-weight:900;font-size:28px;padding:10px 20px;letter-spacing:2px">SITE SIGN-IN</div>
          <h1 style="margin:18px 0 4px;font-size:26px">${job.title}</h1>
          <p style="margin:0 0 18px;color:#555">${job.site_address || ""}</p>
          <img src="${qrSrc}" style="width:320px;height:320px" />
          <p style="font-size:18px;font-weight:bold;margin:16px 0 4px">SCAN TO SIGN IN / OUT</p>
          <p style="color:#555;margin:0">All workers, subbies and visitors must sign in.</p>
        </div>
        <script>setTimeout(() => window.print(), 400);</script>
      </body></html>`);
    w.document.close();
  };

  const exportCsv = async () => {
    try {
      const res = await api.get(`/jobs/${job.id}/signins/export.csv`, { responseType: "blob" });
      const a = document.createElement("a");
      a.href = URL.createObjectURL(res.data);
      a.download = `site-signins-${job.job_number || job.id}.csv`;
      a.click();
    } catch { toast.error("Export failed"); }
  };

  const signOut = async (sid) => {
    try {
      await api.post(`/jobs/${job.id}/signins/${sid}/signout`);
      loadLog();
    } catch { toast.error("Sign-out failed"); }
  };

  const fmtTime = (iso) => iso ? new Date(iso).toLocaleTimeString("en-NZ", { hour: "2-digit", minute: "2-digit" }) : "—";

  return (
    <section className="hard-card p-5" data-testid="job-signin-panel">
      <div className="flex items-center justify-between mb-3">
        <div className="label-tag flex items-center gap-1.5">
          <QrCode className="w-3 h-3" /> Site sign-in
        </div>
        {log && (
          <div className="text-[10px] font-mono uppercase tracking-widest text-zinc-500" data-testid="job-signin-onsite-count">
            {log.on_site.length} on site now
          </div>
        )}
      </div>

      <div className="flex gap-4 items-start">
        {qrSrc && (
          <img src={qrSrc} alt="Site sign-in QR" className="w-28 h-28 border border-zinc-300 shrink-0"
               data-testid="job-signin-qr" />
        )}
        <div className="min-w-0 flex-1 space-y-2">
          <p className="text-xs text-zinc-600">
            Print this QR at the site entrance. Crew get 1-tap sign-in; visitors and subbies enter their details — no app needed.
          </p>
          <div className="flex flex-wrap gap-1.5">
            <Button size="sm" variant="outline" onClick={printQr} disabled={!qrSrc}
                    className="h-8 rounded-sm border-zinc-400 font-bold uppercase tracking-wider text-[10px]"
                    data-testid="job-signin-print-btn">
              <Printer className="w-3.5 h-3.5 mr-1" /> Print poster
            </Button>
            <Button size="sm" variant="outline" onClick={copyUrl} disabled={!url}
                    className="h-8 rounded-sm border-zinc-400 font-bold uppercase tracking-wider text-[10px]"
                    data-testid="job-signin-copy-btn">
              <Copy className="w-3.5 h-3.5 mr-1" /> Copy link
            </Button>
            <Button size="sm" variant="outline" onClick={exportCsv}
                    className="h-8 rounded-sm border-zinc-400 font-bold uppercase tracking-wider text-[10px]"
                    data-testid="job-signin-export-btn">
              <Download className="w-3.5 h-3.5 mr-1" /> CSV
            </Button>
          </div>
        </div>
      </div>

      {/* Live on-site register */}
      {log && log.on_site.length > 0 && (
        <div className="mt-4" data-testid="job-signin-onsite-list">
          <div className="text-[9px] font-bold uppercase tracking-widest text-zinc-500 mb-1.5 flex items-center gap-1">
            <UserCheck className="w-3 h-3 text-green-600" /> On site now
          </div>
          <div className="space-y-1">
            {log.on_site.map((p) => (
              <div key={p.id} className="flex items-center gap-2 text-xs p-1.5 bg-green-50 border border-green-200">
                <span className={`w-1.5 h-1.5 rounded-full ${p.person_type === "worker" ? "bg-[#FF5F15]" : "bg-green-600"}`} />
                <span className="font-bold">{p.name}</span>
                {p.company && <span className="text-zinc-500">· {p.company}</span>}
                <span className="ml-auto font-mono text-zinc-500">in {fmtTime(p.signed_in_at)}</span>
                <button type="button" onClick={() => signOut(p.id)}
                        className="text-[9px] font-bold uppercase tracking-widest text-zinc-400 hover:text-red-600"
                        data-testid={`job-signin-signout-${p.id}`}>
                  Sign out
                </button>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Today's log */}
      {log && log.items.length > 0 && (
        <div className="mt-4">
          <div className="text-[9px] font-bold uppercase tracking-widest text-zinc-500 mb-1.5">Today's log</div>
          <div className="max-h-48 overflow-y-auto space-y-0.5">
            {log.items.map((r) => (
              <div key={r.id} className="flex items-center gap-2 text-[11px] py-1 border-b border-zinc-100" data-testid={`job-signin-log-${r.id}`}>
                <span className="font-medium truncate flex-1">{r.name}{r.company ? ` (${r.company})` : ""}</span>
                <span className="font-mono text-zinc-500">{fmtTime(r.signed_in_at)} → {fmtTime(r.signed_out_at)}</span>
              </div>
            ))}
          </div>
        </div>
      )}
    </section>
  );
}
